// Línguas da plataforma — módulo puro (sem React), importável no browser e no backend (`@ecliptica/i18n/langs`).

/** Todas as línguas com recursos mantidos. Cada texto novo é escrito em TODAS (há testes que comparam as chaves). */
export const LANGS = ["pt", "en"] as const;
export type Lang = (typeof LANGS)[number];

/**
 * Línguas ATIVAS: as únicas que o site, o backoffice e os emails mostram.
 * Por agora só inglês. Para voltar a ligar o português em todo o lado basta acrescentar "pt" aqui
 * (ex.: `["en", "pt"]`): o botão de língua reaparece, o site passa a servir PT em `/pt/…`
 * e o build pré-renderiza também essas páginas. Os recursos PT continuam no repositório.
 */
export const ACTIVE_LANGS: readonly Lang[] = ["en"];

/** Língua por omissão: endereços sem prefixo no site e destino de tudo o que pede uma língua inativa. Tem de estar ativa. */
export const DEFAULT_LANG: Lang = "en";

export const LANG_STORAGE_KEY = "ecliptica.lang";

export function isLang(x: unknown): x is Lang {
  return typeof x === "string" && (LANGS as readonly string[]).includes(x);
}

export function isActiveLang(x: unknown): x is Lang {
  return isLang(x) && ACTIVE_LANGS.includes(x);
}

/** Uma língua ativa: a pedida, se estiver ativa; senão a por omissão (ex.: locale "pt" guardado numa conta antiga → "en"). */
export function activeLangOr(x: unknown): Lang {
  return isActiveLang(x) ? x : DEFAULT_LANG;
}

/* ------------------------------------------------------------------ conteúdo bilingue (PT/EN guardado lado a lado) */
// Textos de CONTEÚDO com uma versão por língua (nomes de papéis, mensagem de manutenção, respostas guardadas…).
// Enquanto uma língua está inativa: não se mostra nem se edita, só as ativas são obrigatórias, e o valor
// guardado dessa língua mantém-se intacto (nunca é apagado ao gravar).

export type LangTexts = Partial<Record<Lang, string>>;

/** Línguas cujo conteúdo se mostra/edita (as ativas, na ordem de LANGS). */
export function contentLangs(): Lang[] {
  return LANGS.filter((l) => ACTIVE_LANGS.includes(l));
}

/** Tem texto (não vazio) em todas as línguas ativas? As inativas são opcionais. */
export function filledInActiveLangs(v: LangTexts | null | undefined): boolean {
  return ACTIVE_LANGS.every((l) => (v?.[l] ?? "").trim().length > 0);
}

/**
 * Junta um texto bilingue enviado (`patch`) ao que estava guardado: as línguas que vierem ganham, as que
 * faltarem (ex.: a inativa, que o backoffice não mostra) ficam como estavam (ou "" se nunca existiram).
 */
export function mergeLangTexts(before: LangTexts | null | undefined, patch: LangTexts): Record<Lang, string> {
  return Object.fromEntries(LANGS.map((l) => [l, patch[l] ?? before?.[l] ?? ""])) as Record<Lang, string>;
}

/** Texto de um conteúdo bilingue numa língua ATIVA (a pedida se ativa, senão a por omissão). Nunca cai para uma inativa. */
export function activeText(v: LangTexts | null | undefined, lang: unknown): string | undefined {
  return v?.[activeLangOr(lang)] || v?.[DEFAULT_LANG] || undefined;
}

/**
 * Cópia de um valor (ex.: "antes"/"depois" da auditoria) sem as chaves de línguas inativas, a qualquer
 * profundidade: `{ labels: { pt: "…", en: "Moderator" } }` → `{ labels: { en: "Moderator" } }`. Só para mostrar.
 */
export function withoutInactiveLangs<T>(v: T): T {
  if (Array.isArray(v)) return v.map((x) => withoutInactiveLangs(x)) as T;
  if (!v || typeof v !== "object") return v;
  return Object.fromEntries(
    Object.entries(v as Record<string, unknown>)
      .filter(([k]) => !isLang(k) || isActiveLang(k))
      .map(([k, x]) => [k, withoutInactiveLangs(x)]),
  ) as T;
}

/** Locale para Intl (datas/números) na língua ativa: "en" (ou "pt-PT" se o PT estiver ativo e for a escolhida). */
export function intlLocale(lang: unknown): string {
  return htmlLang(activeLangOr(typeof lang === "string" ? lang.slice(0, 2) : lang));
}

/** Valor de `<html lang>`. */
export function htmlLang(l: Lang): string {
  return l === "pt" ? "pt-PT" : "en";
}

/** Valor de `og:locale`. */
export function ogLocale(l: Lang): string {
  return l === "pt" ? "pt_PT" : "en_GB";
}
